import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Common
    loading: "Loading...",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    back: "Back",
    submit: "Submit",
    
    // Auth
    login: "Sign In",
    register: "Register",
    logout: "Logout",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot password?",
    
    // Navigation
    home: "Home",
    children: "Children",
    dailySignIn: "Daily Sign-In",
    users: "Staff Management",
    emergencyContacts: "Emergency Contacts",
    settings: "Settings",
    
    // Trial & Subscription
    trialInProgress: "Trial in progress",
    daysLeft: "days left",
    hoursLeft: "hours left",
    trialExpired: "Trial Expired",
    subscribe: "Subscribe",
    subscribeToContinue: "Subscribe to continue using the app",
    choosePlan: "Choose Your Plan",
    currentPlan: "Current Plan",
    upgrade: "Upgrade",
    
    // Plans
    basic: "Basic",
    deluxe: "Deluxe",
    premium: "Premium",
    monthly: "Monthly",
    annual: "Annual",
    perMonth: "/month",
    perYear: "/year",
    maxChildren: "Max Children",
    unlimited: "Unlimited",
    
    // Children
    addChild: "Add Child",
    childName: "Child's Name",
    category: "Category",
    regular: "Regular Daycare",
    schoolAge: "School Age",
    authorizedPickup: "Authorized Pickup Persons",
    noChildren: "No children added yet",
    childLimitReached: "Child limit reached for your plan",
    
    // Sign-in
    dropOff: "Drop Off",
    pickUp: "Pick Up",
    selectPerson: "Select person",
    specialNotes: "Special Notes",
    present: "Present",
    absent: "Absent",
    print: "Print",
    
    // Voice Input
    speakNow: "Speak now!",
    listening: "Listening...",
    stopListening: "Stop listening",
    clickToSpeak: "Click to speak",
    
    // Messages
    savedSuccessfully: "Saved successfully",
    errorOccurred: "An error occurred",
    confirmDelete: "Are you sure you want to delete?",
  },
  es: {
    // Common
    loading: "Cargando...",
    save: "Guardar",
    cancel: "Cancelar",
    delete: "Eliminar",
    edit: "Editar",
    back: "Volver",
    submit: "Enviar",
    
    // Auth
    login: "Iniciar Sesión",
    register: "Registrarse",
    logout: "Cerrar Sesión",
    email: "Correo Electrónico",
    password: "Contraseña",
    forgotPassword: "¿Olvidó su contraseña?",
    
    // Navigation
    home: "Inicio",
    children: "Niños",
    dailySignIn: "Registro Diario",
    users: "Gestión de Personal",
    emergencyContacts: "Contactos de Emergencia",
    settings: "Configuración",
    
    // Trial & Subscription
    trialInProgress: "Período de prueba",
    daysLeft: "días restantes",
    hoursLeft: "horas restantes",
    trialExpired: "Prueba Expirada",
    subscribe: "Suscribirse",
    subscribeToContinue: "Suscríbase para continuar usando la aplicación",
    choosePlan: "Elija Su Plan",
    currentPlan: "Plan Actual",
    upgrade: "Mejorar Plan",
    
    // Plans
    basic: "Básico",
    deluxe: "Deluxe",
    premium: "Premium",
    monthly: "Mensual",
    annual: "Anual",
    perMonth: "/mes",
    perYear: "/año",
    maxChildren: "Máximo de Niños",
    unlimited: "Ilimitado",
    
    // Children
    addChild: "Agregar Niño",
    childName: "Nombre del Niño",
    category: "Categoría",
    regular: "Guardería Regular",
    schoolAge: "Edad Escolar",
    authorizedPickup: "Personas Autorizadas para Recoger",
    noChildren: "No hay niños agregados",
    childLimitReached: "Límite de niños alcanzado para su plan",
    
    // Sign-in
    dropOff: "Entrada",
    pickUp: "Salida",
    selectPerson: "Seleccionar persona",
    specialNotes: "Notas Especiales",
    present: "Presente",
    absent: "Ausente",
    print: "Imprimir",
    
    // Voice Input
    speakNow: "¡Hable ahora!",
    listening: "Escuchando...",
    stopListening: "Dejar de escuchar",
    clickToSpeak: "Clic para hablar",
    
    // Messages
    savedSuccessfully: "Guardado exitosamente",
    errorOccurred: "Ocurrió un error",
    confirmDelete: "¿Está seguro de que desea eliminar?",
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('appLanguage') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('appLanguage', language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'es' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
